import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || '';
const API_KEY = process.env.REACT_APP_API_KEY || 'dev-api-key-12345';

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
    'X-API-Key': API_KEY,
  },
});

export const executeRawQuery = async (sql, database = null) => {
  try {
    const response = await apiClient.post('/api/v1/query/raw', {
      sql,
      database,
    });
    return response.data;
  } catch (error) {
    if (error.response?.data?.detail) {
      const detail = error.response.data.detail;
      if (typeof detail === 'object') {
        throw new Error(detail.error || JSON.stringify(detail));
      }
      throw new Error(detail);
    }
    throw new Error(error.message || 'Query execution failed');
  }
};

export const buildAndExecuteQuery = async (queryConfig) => {
  try {
    const response = await apiClient.post('/api/v1/query/build', queryConfig);
    return response.data;
  } catch (error) {
    if (error.response?.data?.detail) {
      const detail = error.response.data.detail;
      if (typeof detail === 'object') {
        throw new Error(detail.error || JSON.stringify(detail));
      }
      throw new Error(detail);
    }
    throw new Error(error.message || 'Query execution failed');
  }
};

export const validateQuery = async (sql) => {
  try {
    const response = await apiClient.post('/api/v1/query/validate', { sql });
    return response.data;
  } catch (error) {
    throw new Error(error.message || 'Validation failed');
  }
};

export const getTeams = async () => {
  try {
    const response = await apiClient.get('/api/v1/teams');
    return response.data;
  } catch (error) {
    console.error('Failed to fetch teams:', error);
    return [];
  }
};

export const getTables = async () => {
  try {
    const response = await apiClient.get('/api/v1/schema/tables');
    return response.data;
  } catch (error) {
    console.error('Failed to fetch tables:', error);
    return [];
  }
};

export const askAI = async (message, history = []) => {
  try {
    const response = await apiClient.post('/api/v1/ai/chat', {
      message,
      history,
    });
    return response.data;
  } catch (error) {
    if (error.response?.data?.detail) {
      const detail = error.response.data.detail;
      if (typeof detail === 'object') {
        throw new Error(detail.error || JSON.stringify(detail));
      }
      throw new Error(detail);
    }
    throw new Error(error.message || 'AI chat request failed');
  }
};

export const generateDrilldownSql = async (originalSql, clickContext) => {
  try {
    const response = await apiClient.post('/api/v1/ai/drilldown', {
      original_sql: originalSql,
      label_column: clickContext.labelColumn,
      label_value: String(clickContext.labelValue ?? ''),
      group_column: clickContext.groupColumn || null,
      group_value: clickContext.groupValue != null ? String(clickContext.groupValue) : null,
    });
    return response.data;
  } catch (error) {
    if (error.response?.data?.detail) {
      const detail = error.response.data.detail;
      if (typeof detail === 'object') {
        throw new Error(detail.error || JSON.stringify(detail));
      }
      throw new Error(detail);
    }
    throw new Error(error.message || 'Drilldown request failed');
  }
};

export const getTableSchema = async (tableName) => {
  try {
    const response = await apiClient.get(`/api/v1/schema/tables/${tableName}`);
    return response.data;
  } catch (error) {
    console.error(`Failed to fetch schema for ${tableName}:`, error);
    return null;
  }
};

// Dashboard CRUD
export const fetchDashboards = async () => {
  const response = await apiClient.get('/api/v1/dashboards');
  return response.data;
};

export const createDashboard = async (name) => {
  const response = await apiClient.post('/api/v1/dashboards', { name });
  return response.data;
};

export const deleteDashboardApi = async (id) => {
  await apiClient.delete(`/api/v1/dashboards/${id}`);
};

export const updateDashboardApi = async (id, updates) => {
  const response = await apiClient.put(`/api/v1/dashboards/${id}`, updates);
  return response.data;
};

// Widget CRUD
export const fetchWidgets = async (dashboardId) => {
  const params = dashboardId ? { dashboard_id: dashboardId } : {};
  const response = await apiClient.get('/api/v1/widgets', { params });
  return response.data;
};

export const createWidget = async ({ title, sql, chartType, dashboard_id, seriesColors, unit, colSpan, rowSpan }) => {
  const response = await apiClient.post('/api/v1/widgets', { title, sql, chartType, dashboard_id, seriesColors, unit, colSpan, rowSpan });
  return response.data;
};

export const updateWidgetApi = async (id, updates) => {
  const response = await apiClient.put(`/api/v1/widgets/${id}`, updates);
  return response.data;
};

export const deleteWidgetApi = async (id) => {
  await apiClient.delete(`/api/v1/widgets/${id}`);
};
